import { useOutletContext } from "react-router-dom";
import { OutletContextType, schooYearType } from "../../types/GlobalType";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  schoolController,
  schoolYearController,
} from "../../_api/controllers/scolarite.controller.ts";
import { useRef, useState } from "react";
import HasRole from "../../component/_elements/HasRole.tsx";
import moment from "moment";

const ParametresGenerauxSkeleton = ({
  isDarkMode,
}: {
  isDarkMode: boolean;
}) => {
  const baseBg = isDarkMode ? "bg-gray-700" : "bg-gray-300";

  return (
    <div className="animate-pulse">
      <div className="space-y-6">
        {/* Card: Informations établissement */}
        <div
          className={`p-5 rounded-xl border ${
            isDarkMode
              ? "bg-[#0D1117] border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="h-5 w-72 mb-6 rounded bg-gray-300 dark:bg-gray-700"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(4)].map((_, index) => (
              <div key={index}>
                <div className="h-4 w-40 mb-2 rounded bg-gray-300 dark:bg-gray-700"></div>
                <div className={`h-10 w-full rounded-md ${baseBg}`}></div>
              </div>
            ))}
          </div>
        </div>

        {/* Card: Année scolaire (Admin section) */}
        <div
          className={`p-5 rounded-xl border ${
            isDarkMode
              ? "bg-[#0D1117] border-gray-700"
              : "bg-gray-50 border-gray-200"
          }`}
        >
          <div className="h-5 w-48 mb-6 rounded bg-gray-300 dark:bg-gray-700"></div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[...Array(3)].map((_, index) => (
              <div key={index}>
                <div className="h-4 w-40 mb-2 rounded bg-gray-300 dark:bg-gray-700"></div>
                <div className={`h-10 w-full rounded-md ${baseBg}`}></div>
              </div>
            ))}
          </div>

          <div className="mt-4 flex items-center gap-2">
            <div className="h-4 w-4 rounded bg-gray-300 dark:bg-gray-700"></div>
            <div className="h-4 w-72 rounded bg-gray-300 dark:bg-gray-700"></div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Contraintes du logo, partagées entre la validation et le message d'aide affiché.
const LOGO_ACCEPTED_TYPES = ["image/jpeg", "image/jpg", "image/png"];
const LOGO_MAX_SIZE_BYTES = 2 * 1024 * 1024; // 2 Mo

const General: React.FC = () => {
  const { isDarkMode } = useOutletContext<OutletContextType>();
  const [notification, setNotification] = useState<any>(null); // { message, type }
  // Fonction pour afficher une notification

  const showNotification = (message: string, type = "success") => {
    setNotification({ message, type });
    setTimeout(() => setNotification(null), 3000);
  };

  const queryClient = useQueryClient();

  //fetch anne scolaire
  const { data: schoolYearData, isLoading: isLoadingSchoolYear } = useQuery({
    queryKey: ["schoolYears"],
    queryFn: schoolYearController.getAll,
  });

  //fetch school identity
  const { data: schoolData, isLoading: isLoadingSchoolData } = useQuery({
    queryKey: ["school"],
    queryFn: schoolController.getAll,
  });

  const schoolYearArr = schoolYearData?.data || [];
  const schoolArr = schoolData?.data || [];
  const schoolExists = schoolArr.length > 0;

  const selectedSchoolYear = schoolYearArr.find((sy: any) => sy.selected);

  const [selectedYearData, setSelectedYearData] = useState<schooYearType | any>(
    null,
  );

  const mutationSaveSelectedYear = useMutation({
    mutationFn: () => schoolYearController.selectYear(selectedYearData.id),
    onSuccess: () => {
      showNotification("Modification effectuée avec succès!", "success");
      setSelectedYearData(null);
      setShowActiveSYModal(false);
      queryClient.invalidateQueries({ queryKey: ["schoolYears"] });
    },

    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer.", "erreur");
    },
  });

  // ===== Création / modification de l'établissement (popup) =====
  const [showActiveSYModal, setShowActiveSYModal] = useState(false);
  const [showSchoolModal, setShowSchoolModal] = useState(false);
  const [schoolForm, setSchoolForm] = useState({
    designation: "",
    code: "",
    telephone: "",
  });

  const openSchoolModal = () => {
    setSchoolForm({
      designation: schoolArr[0]?.designation || "",
      code: schoolArr[0]?.code || "",
      telephone: schoolArr[0]?.telephone || "",
    });
    setShowSchoolModal(true);
  };

  const mutationSaveSchoolData = useMutation({
    mutationFn: () =>
      schoolController.create({
        designation: schoolForm.designation,
        code: schoolForm.code,
        telephone: schoolForm.telephone,
      }),
    onSuccess: () => {
      showNotification("Enregistrement effectué avec succès!", "success");
      setShowSchoolModal(false);
      queryClient.invalidateQueries({ queryKey: ["school"] });
    },
    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer.", "erreur");
    },
  });

  const mutationUpdateSchoolData = useMutation({
    mutationFn: () =>
      schoolController.update(schoolArr[0]?.id, {
        designation: schoolForm.designation,
        code: schoolForm.code,
        telephone: schoolForm.telephone,
      }),
    onSuccess: () => {
      showNotification("Modification effectuée avec succès!", "success");
      setShowSchoolModal(false);
      queryClient.invalidateQueries({ queryKey: ["school"] });
    },
    onError: (err) => {
      // console.log(err);
      showNotification("Une erreur est survenue, réessayer.", "erreur");
    },
  });

  // ===== Logo de l'établissement (popup indépendant, déclenché sur hover/clic) =====
  // L'upload prend l'id de l'école : il n'est donc possible que si l'école existe déjà.
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);

  const mutationUploadLogo = useMutation({
    // NOTE: mutate() ne transmet qu'une seule valeur à mutationFn — l'appel
    // d'origine `mutationUploadLogo.mutate(id, newRecord)` n'aurait donc jamais
    // transmis `newRecord`. On regroupe les deux dans un seul objet.
    mutationFn: () =>
      schoolController.uploadLogo(schoolArr[0]?.id, { formData }),
    onSuccess: (data) => {
      console.log("Logo uploadé avec succès :", data);
      showNotification("Logo mis à jour avec succès!", "success");
      queryClient.invalidateQueries({ queryKey: ["school"] });
    },
    onError: (err) => {
      // console.log(err);
      showNotification(
        "Une erreur est survenue lors du téléchargement du logo.",
        "erreur",
      );
      setLogoPreview(null);
    },
  });

  // console.log(selectedYearData);
  const handleSave = () => {
    //logic to save general settings
    mutationSaveSelectedYear.mutate();
  };

  const handleSchoolDataSave = () => {
    if (!schoolForm.designation.trim() || !schoolForm.code.trim())
      return showNotification("Le nom et le code sont obligatoires", "erreur");

    if (schoolArr.length === 0) {
      mutationSaveSchoolData.mutate();
    } else {
      mutationUpdateSchoolData.mutate();
    }
  };

  const handleSchoolLogoUpload = () => {
    mutationUploadLogo.mutate();
  };

  const handleLogoButtonClick = () => {
    if (!schoolExists) {
      showNotification("Veuillez d'abord créer l'établissement", "erreur");
      return;
    }
    if (mutationUploadLogo.isPending) return;
    logoInputRef.current?.click();
  };

  let formData = new FormData();
  const handleLogoFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!LOGO_ACCEPTED_TYPES.includes(file.type)) {
      showNotification("Le logo doit être au format JPEG ou PNG", "erreur");
      e.target.value = "";
      return;
    }
    if (file.size > LOGO_MAX_SIZE_BYTES) {
      showNotification("Le logo ne doit pas dépasser 2 Mo", "erreur");
      e.target.value = "";
      return;
    }

    setLogoPreview(URL.createObjectURL(file));
    // const formData = new FormData();
    formData.append("logo", file);
    mutationUploadLogo.mutate();
    handleSchoolLogoUpload();

    // console.log(formData);
    // console.log(file);
    // console.log(schoolArr[0]);
    // console.log("Logo uploadé :", file.name, "(", file.size, "octets )");
    // console.log(logoPreview);

    e.target.value = "";
  };

  const logoSrc = logoPreview || schoolArr[0]?.logo_path || null;

  // Styles communs réutilisés pour une cohérence visuelle
  const cardClass = `p-5 md:p-6 rounded-xl border shadow-sm ${
    isDarkMode ? "bg-[#0D1117] border-gray-700" : "bg-gray-50 border-gray-200"
  }`;

  const labelClass = `block text-xs font-medium mb-1.5 ${
    isDarkMode ? "text-gray-300" : "text-gray-700"
  }`;

  const readOnlyInputClass = `w-full pl-3 pr-9 py-2.5 rounded-md border text-xs cursor-not-allowed ${
    isDarkMode
      ? "bg-[#161B22]/60 border-gray-700 text-gray-400"
      : "bg-gray-100 border-gray-200 text-gray-500"
  } focus:outline-none`;

  const staticValueClass = `flex items-center gap-2 w-full px-3 py-2.5 rounded-md border text-xs ${
    isDarkMode
      ? "bg-[#161B22]/60 border-gray-700 text-gray-300"
      : "bg-gray-100 border-gray-200 text-gray-600"
  }`;

  const editableInputClass = `w-full px-3 py-2.5 rounded-md border text-xs ${
    isDarkMode
      ? "bg-[#161B22] border-gray-700 text-gray-100"
      : "bg-white border-gray-300 text-gray-900"
  } focus:outline-none focus:ring-2 focus:ring-blue-500`;

  const modalCancelBtnClass = `px-4 py-2 rounded-md text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
    isDarkMode
      ? "bg-transparent border border-gray-600 text-gray-300 hover:bg-gray-700"
      : "bg-transparent border border-gray-300 text-gray-600 hover:bg-gray-50"
  }`;

  const modalSaveBtnClass = `flex items-center gap-2 px-4 py-2 rounded-md text-xsxs font-medium text-white transition-colors !rounded-button whitespace-nowrap ${
    isDarkMode
      ? "bg-blue-600 hover:bg-blue-700"
      : "bg-blue-500 hover:bg-blue-600"
  }`;

  console.log("Logo source:", logoSrc);

  const isSavingSchool =
    mutationSaveSchoolData.isPending || mutationUpdateSchoolData.isPending;

  return (
    <>
      {isLoadingSchoolYear || isLoadingSchoolData ? (
        <ParametresGenerauxSkeleton isDarkMode={isDarkMode} />
      ) : (
        <div>
          <div className="flex items-start justify-between gap-4 mb-1">
            <div className="flex flex-col items-start justify-start gap-1">
              <div className="flex items-center gap-3">
                <div
                  className={`h-9 w-9 flex items-center justify-center rounded-lg ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400"
                      : "bg-blue-50 text-blue-600"
                  }`}
                >
                  <i className="fas fa-sliders-h text-sm"></i>
                </div>
                <h2 className="text-md font-medium">Paramètres généraux</h2>
              </div>
              <p
                className={`mb-6 ml-12 text-xs ${
                  isDarkMode ? "text-gray-400" : "text-gray-600"
                }`}
              >
                Configurez les paramètres de base de l'école
              </p>
            </div>

            {/* ===== Logo de l'établissement ===== */}
            {/* <div className="flex flex-col items-end gap-1.5 flex-shrink-0 mt-[-10px]">
              {logoSrc ? (
                <div
                  className={`relative h-16 w-16 rounded-xl overflow-hidden border-2 group ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <img
                    src={logoSrc}
                    alt="Logo de l'établissement"
                    className="h-full w-full object-cover"
                  />
                  <button
                    type="button"
                    onClick={handleLogoButtonClick}
                    title="Modifier le logo"
                    className="absolute inset-0 flex flex-col items-center justify-center gap-0.5 bg-black/0 group-hover:bg-black/55 opacity-0 group-hover:opacity-100 transition-all text-white"
                  >
                    <i className="fas fa-pen text-xs"></i>
                    <span className="text-[10px] font-medium">Modifier</span>
                  </button>
                  {mutationUploadLogo.isPending && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/60">
                      <i className="fas fa-spinner fa-spin text-white text-sm"></i>
                    </div>
                  )}
                </div>
              ) : (
                <button
                  type="button"
                  onClick={handleLogoButtonClick}
                  disabled={!schoolExists}
                  title={
                    schoolExists
                      ? "Ajouter un logo"
                      : "Créez d'abord l'établissement"
                  }
                  className={`flex items-center gap-2 px-3 py-2 rounded-lg text-xs font-medium border-2 border-dashed transition-colors !rounded-button whitespace-nowrap ${
                    !schoolExists
                      ? isDarkMode
                        ? "border-gray-800 text-gray-700 cursor-not-allowed"
                        : "border-gray-200 text-gray-300 cursor-not-allowed"
                      : isDarkMode
                        ? "border-gray-700 text-gray-400 hover:border-blue-500/50 hover:text-blue-400"
                        : "border-gray-300 text-gray-500 hover:border-blue-300 hover:text-blue-600"
                  }`}
                >
                  {mutationUploadLogo.isPending ? (
                    <i className="fas fa-spinner fa-spin text-sm"></i>
                  ) : (
                    <i className="fas fa-image text-sm"></i>
                  )}
                  Ajouter un logo
                </button>
              )}
              <span
                className={`text-[11px] ${
                  isDarkMode ? "text-gray-500" : "text-gray-400"
                }`}
              >
                JPEG/PNG, 2 Mo max
              </span>
              <input
                ref={logoInputRef}
                type="file"
                accept="image/jpeg,image/jpg,image/png"
                onChange={handleLogoFileChange}
                className="hidden"
              />
            </div> */}
          </div>

          <div className="space-y-6">
            {/* ===== Carte : Informations établissement ===== */}
            <div className={cardClass}>
              <div className="flex items-center justify-between mb-5">
                <h3 className="flex items-center gap-2 text-sm font-semibold">
                  <i
                    className={`fas fa-school text-sm ${
                      isDarkMode ? "text-blue-400" : "text-blue-600"
                    }`}
                  ></i>
                  Informations de l'établissement
                </h3>
                <button
                  onClick={openSchoolModal}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
                    isDarkMode
                      ? "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                      : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                  }`}
                >
                  <i
                    className={`fas ${
                      schoolExists ? "fa-edit" : "fa-plus"
                    } text-xs`}
                  ></i>
                  {schoolExists ? "Modifier école" : "Ajouter école"}
                </button>
              </div>

              {!schoolExists && (
                <div
                  className={`mb-5 flex items-center gap-2 px-3 py-2.5 rounded-md text-sm ${
                    isDarkMode
                      ? "bg-yellow-500/10 text-yellow-400"
                      : "bg-yellow-50 text-yellow-700"
                  }`}
                >
                  <i className="fas fa-circle-info"></i>
                  Aucun établissement enregistré pour le moment. Cliquez sur
                  "Ajouter école" pour commencer.
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                <div>
                  <label className={labelClass}>Nom de l'établissement</label>
                  <div className="relative">
                    <input
                      type="text"
                      value={schoolArr[0]?.designation || ""}
                      placeholder="Non renseigné"
                      className={readOnlyInputClass}
                      readOnly
                    />
                    <i
                      className={`fas fa-lock absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
                        isDarkMode ? "text-gray-600" : "text-gray-400"
                      }`}
                    ></i>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Type d'établissement</label>
                  <div
                    className={`relative border rounded-md ${
                      isDarkMode ? "border-gray-700" : "border-gray-300"
                    }`}
                  >
                    <select
                      className={`appearance-none w-full px-3 py-1 text-sm rounded-md ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    >
                      {/* <option value="college">Collège</option>
                          <option value="lycee">Lycée</option>
                          <option value="primaire">École primaire</option> */}
                      <option value="secondaire">École secondaire</option>
                      {/* <option value="maternelle">École maternelle</option> */}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                      <i className="fas fa-chevron-down text-xs"></i>
                    </div>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Code</label>
                  <div className="relative">
                    <input
                      readOnly
                      type="text"
                      value={schoolArr[0]?.code || ""}
                      placeholder="Non renseigné"
                      className={readOnlyInputClass}
                    />
                    <i
                      className={`fas fa-lock absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
                        isDarkMode ? "text-gray-600" : "text-gray-400"
                      }`}
                    ></i>
                  </div>
                </div>

                <div>
                  <label className={labelClass}>Téléphone</label>
                  <div className="relative">
                    <input
                      readOnly
                      type="text"
                      value={schoolArr[0]?.telephone || ""}
                      placeholder="Non renseigné"
                      className={readOnlyInputClass}
                    />
                    <i
                      className={`fas fa-lock absolute right-3 top-1/2 -translate-y-1/2 text-xs ${
                        isDarkMode ? "text-gray-600" : "text-gray-400"
                      }`}
                    ></i>
                  </div>
                </div>
              </div>
            </div>

            {/* ===== Carte : Année scolaire (Admin) ===== */}
            <HasRole allowedRoles={["Administrateur"]}>
              <div className={cardClass}>
                <div className="flex items-center justify-between mb-5">
                  <h3 className="flex items-center gap-2 text-sm font-semibold">
                    <i
                      className={`fas fa-calendar-days text-sm ${
                        isDarkMode ? "text-blue-400" : "text-blue-600"
                      }`}
                    ></i>
                    Année scolaire à afficher par défaut à l'ouverture de
                    l'application
                  </h3>
                  <button
                    onClick={() => setShowActiveSYModal(true)}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors !rounded-button whitespace-nowrap ${
                      isDarkMode
                        ? "bg-blue-500/10 text-blue-400 hover:bg-blue-500/20"
                        : "bg-blue-50 text-blue-600 hover:bg-blue-100"
                    }`}
                  >
                    <i
                      className={`fas ${
                        schoolExists ? "fa-edit" : "fa-plus"
                      } text-xs`}
                    ></i>
                    Modifier année scolaire
                  </button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
                  <div>
                    <label className={labelClass}>Année scolaire</label>
                    <div
                      className={`relative border rounded-md ${
                        isDarkMode ? "border-gray-700" : "border-gray-300"
                      }`}
                    >
                      <select
                        disabled
                        className={`${staticValueClass} appearance-none cursor-not-allowed h-10`}
                        onChange={(e) => {
                          const value = e.target.value;
                          if (!value) {
                            setSelectedYearData(null);
                            return;
                          }
                          const obj = JSON.parse(value);
                          setSelectedYearData(obj);
                        }}
                      >
                        {
                          <option value={""}>
                            selectionner une année scolaire
                          </option>
                        }
                        {schoolYearArr.map((yearItem: any) => {
                          return (
                            <option
                              selected={yearItem.selected ? true : false}
                              key={yearItem.id}
                              value={JSON.stringify(yearItem)}
                            >
                              {yearItem.designation}
                            </option>
                          );
                        })}
                      </select>
                      {/* <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                        <i className="fas fa-chevron-down text-xs"></i>
                      </div> */}
                    </div>
                  </div>

                  <div className="flex flex-col flex-1">
                    <label className={labelClass}>Date de début</label>
                    <span className={staticValueClass}>
                      <i
                        className={`fas fa-calendar text-xs ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      ></i>
                      {moment(selectedSchoolYear?.date_debut).format(
                        "DD-MM-YYYY",
                      ) || "2000-01-01"}
                    </span>
                  </div>

                  <div className="flex flex-col flex-1">
                    <label className={labelClass}>Date de fin</label>
                    <span className={staticValueClass}>
                      <i
                        className={`fas fa-calendar-check text-xs ${
                          isDarkMode ? "text-gray-500" : "text-gray-400"
                        }`}
                      ></i>
                      {moment(selectedSchoolYear?.date_fin).format(
                        "DD-MM-YYYY",
                      ) || "2000-01-01"}
                    </span>
                  </div>
                </div>

                <div
                  className={`mt-5 pt-4 border-t ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <label
                    className={`flex items-center cursor-pointer ${
                      isDarkMode ? "text-gray-300" : "text-gray-700"
                    }`}
                  >
                    <input
                      type="checkbox"
                      defaultChecked={true}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                    />
                    <span className="ml-2 text-sm">
                      Autoriser l'accès aux années précédentes
                    </span>
                  </label>
                </div>
              </div>
            </HasRole>
          </div>

          {/* ===== Popup : créer / modifier l'établissement ===== */}
          {showSchoolModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#1c2128]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-md overflow-hidden`}
              >
                <div
                  className={`px-6 py-5 border-b flex justify-between items-center ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <h3 className="text-lg font-semibold flex items-center gap-2">
                    <i className="fas fa-school text-blue-500"></i>
                    {schoolExists
                      ? "Modifier l'établissement"
                      : "Ajouter l'établissement"}
                  </h3>
                  <button
                    onClick={() => setShowSchoolModal(false)}
                    className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  <div>
                    <label className={labelClass}>
                      Nom de l'établissement{" "}
                      <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={schoolForm.designation}
                      onChange={(e) =>
                        setSchoolForm((prev) => ({
                          ...prev,
                          designation: e.target.value,
                        }))
                      }
                      placeholder="Ex: Ecole Technique Professionnelle Des Metiers"
                      className={editableInputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>
                      Code <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={schoolForm.code}
                      onChange={(e) =>
                        setSchoolForm((prev) => ({
                          ...prev,
                          code: e.target.value,
                        }))
                      }
                      placeholder="Ex: ETPM"
                      className={editableInputClass}
                    />
                  </div>
                  <div>
                    <label className={labelClass}>Téléphone</label>
                    <input
                      type="tel"
                      value={schoolForm.telephone}
                      onChange={(e) =>
                        setSchoolForm((prev) => ({
                          ...prev,
                          telephone: e.target.value,
                        }))
                      }
                      placeholder="+243 ..."
                      className={editableInputClass}
                    />
                  </div>
                </div>

                <div
                  className={`px-6 py-4 border-t flex justify-end gap-3 ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <button
                    disabled={isSavingSchool}
                    onClick={() => setShowSchoolModal(false)}
                    className={`${modalCancelBtnClass} ${
                      isSavingSchool ? "cursor-not-allowed opacity-60" : ""
                    }`}
                  >
                    Annuler
                  </button>
                  <button
                    disabled={isSavingSchool}
                    onClick={handleSchoolDataSave}
                    className={`${modalSaveBtnClass} ${
                      isSavingSchool ? "cursor-not-allowed opacity-70" : ""
                    }`}
                  >
                    {isSavingSchool ? (
                      <>
                        <i className="fas fa-spinner fa-spin text-xs"></i>
                        Enregistrement...
                      </>
                    ) : (
                      <>
                        <i className="fas fa-check text-xs"></i>
                        Enregistrer
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          )}
          {/* ===== Popup : créer / modifier l'établissement ===== */}
          {showActiveSYModal && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div
                className={`${
                  isDarkMode ? "bg-[#1c2128]" : "bg-white"
                } rounded-2xl shadow-2xl w-full max-w-md overflow-hidden`}
              >
                <div
                  className={`px-6 py-5 border-b flex justify-between items-center ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <h3 className="text-md font-semibold flex items-center gap-2">
                    <i className="fas fa-school text-blue-500"></i>
                    Modifier l'année scolaire active
                  </h3>
                  <button
                    onClick={() => setShowActiveSYModal(false)}
                    className={`h-8 w-8 rounded-full flex items-center justify-center transition-colors ${
                      isDarkMode
                        ? "text-gray-400 hover:bg-gray-700 hover:text-gray-200"
                        : "text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                    }`}
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>

                <div className="px-6 py-5 space-y-4">
                  {/* <label className={labelClass}>
                    Année scolaire <span className="text-red-500">*</span>
                  </label> */}
                  <div
                    className={`relative border rounded-md ${
                      isDarkMode ? "border-gray-700" : "border-gray-300"
                    }`}
                  >
                    <select
                      className={`appearance-none w-full px-3 py-1 text-sm rounded-md ${
                        isDarkMode ? "bg-[#161B22]" : "bg-white"
                      } focus:outline-none focus:ring-2 focus:ring-blue-500`}
                      onChange={(e) => {
                        const value = e.target.value;
                        if (!value) {
                          setSelectedYearData(null);
                          return;
                        }
                        const obj = JSON.parse(value);
                        setSelectedYearData(obj);
                      }}
                    >
                      {
                        <option value={""}>
                          selectionner une année scolaire
                        </option>
                      }
                      {schoolYearArr.map((yearItem: any) => {
                        return (
                          <option
                            selected={yearItem.selected ? true : false}
                            key={yearItem.id}
                            value={JSON.stringify(yearItem)}
                          >
                            {yearItem.designation}
                          </option>
                        );
                      })}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-3 text-gray-500">
                      <i className="fas fa-chevron-down text-xs"></i>
                    </div>
                  </div>
                </div>

                <div
                  className={`px-6 py-2 border-t flex justify-end gap-3 ${
                    isDarkMode ? "border-gray-700" : "border-gray-200"
                  }`}
                >
                  <button
                    disabled={mutationSaveSelectedYear.isPending}
                    onClick={() => setShowActiveSYModal(false)}
                    className={` flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-md font-medium text-sm  ${
                      mutationSaveSelectedYear.isPending
                        ? "cursor-not-allowed opacity-60"
                        : ""
                    }`}
                  >
                    Annuler
                  </button>

                  <HasRole allowedRoles={["Administrateur"]}>
                    <div className="flex justify-end space-x-3">
                      <button
                        disabled={
                          mutationSaveSelectedYear.isPending ||
                          !selectedYearData
                        }
                        onClick={() => {
                          handleSave();
                        }}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-md font-medium text-sm transition-colors ${
                          isDarkMode
                            ? "bg-blue-600 hover:bg-blue-700"
                            : "bg-blue-500 hover:bg-blue-600"
                        } text-white !rounded-button whitespace-nowrap ${
                          mutationSaveSelectedYear.isPending ||
                          !selectedYearData
                            ? "cursor-not-allowed opacity-50"
                            : ""
                        }`}
                      >
                        {mutationSaveSelectedYear.isPending ? (
                          <>
                            <i className="fas fa-spinner fa-spin text-xs"></i>
                            Enregistrement en cours...
                          </>
                        ) : (
                          <>
                            <i className="fas fa-check text-xs"></i>
                            Enregistrer les modifications
                          </>
                        )}
                      </button>
                    </div>
                  </HasRole>
                </div>
              </div>
            </div>
          )}

          {/* ===== NOTIFICATION ===== */}
          {notification && (
            <div
              className={`fixed bottom-4 text-[14px] right-4 flex items-center gap-2 px-5 py-3 rounded-lg text-white shadow-lg transition-all duration-500 ${
                notification.type === "success" ? "bg-green-500" : "bg-red-500"
              }`}
            >
              <i
                className={`fas ${
                  notification.type === "success"
                    ? "fa-circle-check"
                    : "fa-circle-exclamation"
                }`}
              ></i>
              {notification.message}
            </div>
          )}
        </div>
      )}
    </>
  );
};

export default General;
